import { useEffect, useState, useRef } from "react";
import api from "../Api";

/**
 * Offline + Sync manager hook
 * Responsible for:
 * - Offline preload
 * - IndexedDB population
 * - Sync queue handling
 * - Pending count
 * - Network recovery
 */
export default function useOfflineScanner(selectedDate) {
  const [offlineReady, setOfflineReady] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState("idle"); // idle | syncing | complete | error

  const syncingRef = useRef(false);

  /* -------------------- OFFLINE PRELOAD -------------------- */
  const preloadOfflineData = async () => {
    console.log("🟡 [OFFLINE] preload started");

    try {
      setOfflineReady(false);
      setPreloadProgress(10);

      const storedDate = localStorage.getItem("offline_date");

      // 🔁 Date changed → wipe old offline cache
      if (storedDate !== selectedDate) {
        console.log("🧹 [OFFLINE] clearing IndexedDB");
        const { clearOrders, clearQueue } = await import("../utils/scannerDB");
        await clearOrders();
        await clearQueue();
      }

      localStorage.setItem("offline_date", selectedDate);
      setPreloadProgress(30);

      console.log("📡 [OFFLINE] fetching API data");
      const res = await api.get("/api/offline-scan-data", {
        params: { date: selectedDate },
      });

      const orders = Array.isArray(res.data?.orders) ? res.data.orders : [];
      setPreloadProgress(60);

      const { putOrders } = await import("../utils/scannerDB");
      await putOrders(orders);

      setPreloadProgress(100);
      setOfflineReady(true);

      console.log(`✅ [OFFLINE] preload complete (${orders.length})`);
    } catch (err) {
      console.error("❌ [OFFLINE] preload failed", err);
      setPreloadProgress(0);
      setOfflineReady(false);
    }
  };

  /* -------------------- SYNC QUEUE -------------------- */
  const syncScanQueue = async () => {
    if (!navigator.onLine) return;
    if (syncingRef.current) return;

    syncingRef.current = true;
    setSyncStatus("syncing");

    try {
      const { getQueue, removeFromQueue } = await import("../utils/scannerDB");
      const queue = await getQueue();

      if (!queue.length) {
        setSyncStatus("complete");
        syncingRef.current = false;
        return;
      }

      // sync only selected date
      const dateQueue = queue.filter(q => q.date === selectedDate);
      if (!dateQueue.length) {
        setSyncStatus("idle");
        syncingRef.current = false;
        return;
      }

      const payload = dateQueue.map(q => ({
        tracking_id: q.tracking_id,
        account: q.account,
        date: q.date,
        scanned_on: q.time,
      }));

      const res = await api.post("/api/sync-scans", payload);

      if (res.data?.success) {
        for (const item of dateQueue) {
          if (item.__key !== undefined) {
            await removeFromQueue(item.__key);
          }
        }

        console.log(`✅ [SYNC] synced ${payload.length} scans`);
        setSyncStatus("complete");
        setTimeout(() => setSyncStatus("idle"), 1500);
      } else {
        throw new Error("Backend rejected sync");
      }
    } catch (err) {
      console.warn("⏳ [SYNC] failed, will retry", err.message);
      setSyncStatus("error");
    } finally {
      syncingRef.current = false;
    }
  };

  /* -------------------- PENDING COUNT -------------------- */
  useEffect(() => {
    const updatePending = async () => {
      const { getQueue } = await import("../utils/scannerDB");
      const queue = await getQueue();
      setPendingCount(queue.length);
    };

    updatePending();
    const interval = setInterval(updatePending, 3000);
    return () => clearInterval(interval);
  }, []);

  /* -------------------- AUTO SYNC LOOP -------------------- */
  useEffect(() => {
    if (!offlineReady) return;

    const interval = setInterval(syncScanQueue, 4000);
    window.addEventListener("online", syncScanQueue);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", syncScanQueue);
    };
  }, [offlineReady, selectedDate]);

  return {
    offlineReady,
    preloadProgress,
    pendingCount,
    syncStatus,
    preloadOfflineData,
    syncScanQueue,
  };
}
